

import { Component, Vue } from 'vue-property-decorator';
import Loading from '@/components/Loading.vue';
import availableRoutes from '@/plugins/availableRoutes';
import { ROLE } from '@/assets/ts/enum/role';
import moment from 'moment/moment';
import { Project, ProjectGroup, User } from '@/clients/omb-api';
import Api from '@/assets/ts/Api';
import InputButton from '@/components/input/InputButton.vue';
import EditProjectGroupDialog from '@/components/dialogs/EditProjectGroupDialog.vue';
import Collapsable from '@/components/Collapsable.vue';
import InputCheckbox from '@/components/input/InputCheckbox.vue';
import ProjectGroupDetailsDocuments from '@/views/projectgroupdetails/ProjectGroupDetailsDocuments.vue';
import ProjectGroupDetailsTopInfo from '@/views/projectgroupdetails/ProjectGroupDetailsTopInfo.vue';
import ProjectGroupDetailsAssembly from '@/views/projectgroupdetails/ProjectGroupDetailsAssembly.vue';
import ProjectGroupDetailsProduction from '@/views/projectgroupdetails/ProjectGroupDetailsProduction.vue';
import ProjectGroupDetailsArchive from '@/views/projectgroupdetails/ProjectGroupDetailsArchive.vue';
import getRole from '@/plugins/roleHandler';
import EditProjectDialog from '@/components/dialogs/EditProjectDialog.vue';
import DeleteProjectGroupDialog from '@/components/dialogs/DeleteProjectGroupDialog.vue';
import ProjectGroupDetailsReports from '@/views/projectgroupdetails/ProjectGroupDetailsReports.vue';
import OfflineActionHandler from '@/assets/ts/OfflineActionHandler';
import ArchiveProjectGroupDialog from '@/components/dialogs/ArchiveProjectGroupDialog.vue';
import RecoverProjectGroupDialog from '@/components/dialogs/RecoverProjectGroupDialog.vue';

@Component({
    components: {
        RecoverProjectGroupDialog,
        ArchiveProjectGroupDialog,
        ProjectGroupDetailsReports,
        DeleteProjectGroupDialog,
        EditProjectDialog,
        ProjectGroupDetailsArchive,
        ProjectGroupDetailsProduction,
        ProjectGroupDetailsAssembly,
        ProjectGroupDetailsTopInfo,
        ProjectGroupDetailsDocuments,
        InputCheckbox,
        Collapsable,
        EditProjectGroupDialog,
        InputButton,
        Loading,
    },
})
export default class ProjectGroupDetails extends Vue {

    private ROLE = ROLE;
    private availableRoutes = availableRoutes;

    private loading: boolean = true;

    private projectGroups: ProjectGroup[] = [];
    private projectGroup: ProjectGroup | null = null;
    private projects: Project[] | null = null;
    private currentUser: User | null = null;
    private role: ROLE = ROLE.GUEST;
    private currentWeek: number = 0;
    private deleteDialogVisible = false;
    private editDialogVisible = false;
    private archiveDialogVisible = false;
    private recoverDialogVisible = false;
    private newProjectDialogVisible = false;
    private tablesLoaded = false;

    private get allProjectsArchived(): boolean {
        if (!this.projects) return false;

        const filtered = this.projects.filter((p) => p.archivedAt);
        return filtered.length === this.projects.length;
    }

    private get isAtLeastPlanner(): boolean {
        return [ROLE.ADMINISTRATOR, ROLE.PLANNING, ROLE.EPLANNING].includes(this.role);
    }

    private get canReadWriteReports(): boolean {
        return this.isAtLeastPlanner || this.role === ROLE.ASSEMBLY || this.role === ROLE.EASSEMBLY;
    }

    private async created() {
        this.$root.$on('updateProject', this.onProjectUpdate);
        this.$root.$on('openNewProjectDialog', this.openNewProjectDialog);

        this.currentUser = await Api.omb.getCurrentUser();
        this.role = await getRole();
        await this.loadData();
    }

    private destroyed() {
        this.$root.$off('openNewProjectDialog', this.openNewProjectDialog);
    }

    private async loadData() {
        this.loading = true;

        this.currentWeek = moment()
            .isoWeek();
        this.projectGroups = await Api.omb.getAllProjectGroups();
        await this.loadProjectGroup();
        this.projects = await Api.omb.getAllProjectsByGroup(this.projectGroup?.id || 0);

        this.loading = false;
    }

    private async loadProjectGroup() {
        try {
            this.projectGroup = await Api.omb.getProjectGroupBySlug(this.$route.params.slug);
            if (this.projectGroup.archivedAt && this.$route.name === this.availableRoutes.projectGroupDetails) {
                this.$router.push({ name: this.availableRoutes.projectGroupDetailsArchive, params: { slug: this.projectGroup.slug ?? '' } });
            } else if (!this.projectGroup.archivedAt && this.$route.name === this.availableRoutes.projectGroupDetailsArchive) {
                this.$router.push({ name: this.availableRoutes.projectGroupDetails, params: { slug: this.projectGroup.slug ?? '' } });
            }
        } catch (error) {
            console.error(error);
            this.$router.push({ name: this.$route.name === this.availableRoutes.projectGroupDetails ? availableRoutes.projectGroups : availableRoutes.projectGroupsArchive });
        }
    }

    private showDeleteDialog() {
        if (!window.navigator.onLine) {
            OfflineActionHandler.handle('Bauvorhaben löschen');
            return;
        }

        this.deleteDialogVisible = true;
    }

    private hideDeleteDialog() {
        this.deleteDialogVisible = false;
    }

    private onArchived() {
        window.location.reload();
    }

    private onRecovered() {
       window.location.reload();
    }

    private onDeleted() {
        this.$router.push({ name: availableRoutes.projectGroups });
    }

    private showEditDialog() {
        if (!window.navigator.onLine) {
            OfflineActionHandler.handle('Bauvorhaben bearbeiten');
            return;
        }

        this.editDialogVisible = true;
    }

    private hideEditDialog() {
        this.editDialogVisible = false;
    }

    private showArchiveDialog() {
        if (!window.navigator.onLine) {
            OfflineActionHandler.handle('Bauvorhaben archivieren');
            return;
        }

        this.archiveDialogVisible = true;
    }

    private hideArchiveDialog() {
        this.archiveDialogVisible = false;
    }

    private showRecoverDialog() {
        if (!window.navigator.onLine) {
            OfflineActionHandler.handle('Bauvorhaben zurücksetzen');
            return;
        }

        this.recoverDialogVisible = true;
    }

    private hideRecoverDialog() {
        this.recoverDialogVisible = false;
    }

    private onTableLoad() {
        this.tablesLoaded = true;
    }

    private onProjectUpdate(project: Project) {
        if (!this.projects) this.projects = [];

        const index = this.projects.findIndex((p) => p.id === project.id);
        if (index >= 0) {
            this.projects[index] = project;
        } else {
            this.projects.push(project);
        }
    }

    private openNewProjectDialog() {
        this.newProjectDialogVisible = true;
    }

    private showRepairsAndProjectsNear(projectGroup: ProjectGroup) {
        this.$router.push({
            name: availableRoutes.repairsAndProjectsNear,
            query: { projectGroup: `${projectGroup.slug}` },
        });
    }

}
